import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useCurrentUser from 'hooks/useCurrentUser';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { canShowGroupPracticeView } from 'utils/common';
import { ERoleCode } from 'types/enumItems';
import s from './s.module.less';
import SoloPerformance from '../SoloPerformance';
import GroupPerformance from '../GroupPerformance';
import SoloCampaignSetup from '../CampaignSettings/SoloCampaignSetup';
import GroupCampaignSetup from '../CampaignSettings/GroupCampaignSetup';
import PatientLimits from '../PatientLimits';
import LimitReachedCallout from '../PatientLimits/LimitReachedCallout';

type ViewKey = 'practice' | 'my';
type ChipKey = 'performance' | 'prolisting' | 'limits';

const VIEW_TABS: { key: ViewKey; label: string }[] = [
    { key: 'practice', label: 'Practice View' },
    { key: 'my', label: 'My Marketing' },
];

const CHIPS: { key: ChipKey; label: string }[] = [
    { key: 'performance', label: 'Performance' },
    { key: 'prolisting', label: 'Pro-Listing settings' },
    { key: 'limits', label: 'Patient limits' },
];

const KlarityMarketing = () => {
    const [searchParams] = useSearchParams();
    const [currentUser] = useCurrentUser();
    const [homeInfo] = useProviderHomeInfo();

    const isGroupPractice = canShowGroupPracticeView(currentUser);
    const isReadOnly = !!currentUser?.groupPractice || currentUser?.roleCode === ERoleCode.GROUP_PROVIDER;
    const isStaff = isGroupPractice && currentUser?.roleCode !== ERoleCode.GROUP_OWNER;
    const isOwner = currentUser?.roleCode === ERoleCode.GROUP_OWNER;

    // Patient limits 只对 PPA（收 lead gen fee）生效；PPV 不收费也没有上限概念，整块隐藏。
    // practice 不混合 PPA/PPV（见 klarity-practice-org-model），owner/成员自己的 ppaProvider 即代表 practice。
    const isPpa = !!homeInfo?.ppaProvider;
    const visibleChips = isPpa ? CHIPS : CHIPS.filter((c) => c.key !== 'limits');

    const tabParam = searchParams.get('tab');
    const initTab: ChipKey = visibleChips.some((c) => c.key === tabParam) ? (tabParam as ChipKey) : 'performance';
    const [activeChip, setActiveChip] = useState<ChipKey>(initTab);
    const [activeView, setActiveView] = useState<ViewKey>('practice');

    // Staff (non-owner group practice members) can only see the Practice View, not My Marketing.
    const visibleViewTabs = isStaff ? VIEW_TABS.filter((tab) => tab.key === 'practice') : VIEW_TABS;
    const showViewTabs = isGroupPractice;
    const isPracticeView = showViewTabs && (isStaff || activeView === 'practice');
    return (
        <div className={s.container}>
            {showViewTabs && (
                <div className={s.viewTabs}>
                    {visibleViewTabs.map((tab) => (
                        <button
                            type="button"
                            key={tab.key}
                            className={`${s.viewTab} ${activeView === tab.key ? s.viewTabActive : ''}`}
                            onClick={() => {
                                setActiveView(tab.key);
                                setActiveChip('performance');
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            <div className={s.chips}>
                {visibleChips.map((chip) => (
                    <button
                        type="button"
                        key={chip.key}
                        className={`${s.chip} ${activeChip === chip.key ? s.chipActive : ''}`}
                        onClick={() => setActiveChip(chip.key)}
                    >
                        {chip.label}
                    </button>
                ))}
            </div>

            {activeChip === 'performance' &&
                (isPracticeView ? (
                    <GroupPerformance onSetupClick={() => setActiveChip('prolisting')} />
                ) : (
                    <>
                        {/* 被周上限暂停时，Performance 顶部也要说这件事 ——
                            provider 习惯在这一屏确认自己在不在线上（原型 #perfLimitCallout）。
                            Practice View 的 Performance 不在本次范围内。仅 PPA。 */}
                        {isPpa && <LimitReachedCallout onRaise={() => setActiveChip('limits')} />}
                        <SoloPerformance onSetupClick={() => setActiveChip('prolisting')} isReadOnly={isReadOnly} isOwner={isOwner} />
                    </>
                ))}

            {activeChip === 'prolisting' &&
                (isPracticeView ? (
                    <GroupCampaignSetup />
                ) : (
                    <SoloCampaignSetup isReadOnly={isReadOnly} isGroupPractice={isGroupPractice} />
                ))}

            {isPpa && activeChip === 'limits' && (
                <PatientLimits isPracticeView={isPracticeView} isGroupPractice={isGroupPractice} />
            )}
        </div>
    );
};

export default KlarityMarketing;
